import { Alert, Card, Col, ConfigProvider, DatePicker, Divider, Form, Radio, Row, Statistic, Typography } from "antd";
import SDIModal from "../../../../components/ui/SDIModal";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";
import AvenantAgencySelector from "./AvenantAgencySelector";

const { Text, Title } = Typography;

const RemiseEnVigueur = (props) => {
    const [modalRemiseEnVigueur, setModalRemiseEnVigueur] = useState(props.open);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // États pour les données du contrat
    const [contratSuspendu, setContratSuspendu] = useState(null);
    const [dureeeSuspension, setDureeSuspension] = useState(0);
    const [optionRemise, setOptionRemise] = useState(1);
    const [dateRemiseEnVigueur, setDateRemiseEnVigueur] = useState(dayjs());
    const [preview, setPreview] = useState(null);
    const [fraisAccessoires, setFraisAccessoires] = useState(0);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [isContratSuspendu, setIsContratSuspendu] = useState(true);

    // Fonction pour calculer la durée de suspension
    const calculerDureeSuspension = (contrat, dateRemise) => {
        if (!contrat) return 0;
        const dateSuspension = dayjs(contrat.dateEffet);
        return dateRemise.diff(dateSuspension, 'day');
    };

    // Fonction pour déterminer l'option par défaut
    const determinerOptionParDefaut = (duree) => {
        return duree <= 28 ? 1 : 2;
    };

    // useEffect pour charger les données initiales
    useEffect(() => {
        setModalRemiseEnVigueur(props.open);

        if (props.open && props?.contrat?.codeId && !isDataLoaded) {
            console.log('Chargement des données du contrat:', props?.contrat);
            // Vérifier si le contrat est suspendu
            if (props.contrat.avenant?.code !== "SP" || !props.contrat.suspendu) {
                console.log('Contrat non suspendu:', props.contrat.statut);
                setIsContratSuspendu(false);
                return;
            }

            // Réinitialiser les états
            setIsDataLoaded(false);
            setContratSuspendu(null);
            setPreview(null);

            // Charger la prime nette
            axiosInstance.get(`/api/contrat/prime-nette-annuelle/${props?.contrat?.codeId}`)
                .then((res) => {
                    const contratAvecPrime = { ...props.contrat, primeNette: res.data };

                    // Charger les frais accessoires
                    return axiosInstance.get(`/api/avenant/accessoires/RV/${props?.contrat?.engins[0]?.categorie?.codeCategorie}`)
                        .then(response => {
                            console.log('Frais accessoires:', response.data);
                            setFraisAccessoires(response.data);

                            // Calculer la durée de suspension avec la date actuelle
                            const duree = calculerDureeSuspension(contratAvecPrime, dateRemiseEnVigueur);
                            console.log('Durée calculée:', duree);

                            // Mettre à jour tous les états
                            setContratSuspendu(contratAvecPrime);
                            setDureeSuspension(duree);
                            setOptionRemise(determinerOptionParDefaut(duree));
                            setIsDataLoaded(true);

                            console.log('Option sélectionnée:', determinerOptionParDefaut(duree));
                            console.log('Données chargées complètement');
                        });
                })
                .catch((err) => {
                    console.error('Erreur lors du chargement:', err);
                    setIsDataLoaded(false);
                });
        }
    }, [props.open, props?.contrat?.codeId]);

    // useEffect pour recalculer quand la date change
    useEffect(() => {
        if (contratSuspendu && isDataLoaded) {
            const nouvelleDuree = calculerDureeSuspension(contratSuspendu, dateRemiseEnVigueur);
            console.log('Recalcul de la durée:', nouvelleDuree);

            setDureeSuspension(nouvelleDuree);

            // Ajuster l'option si nécessaire
            if (nouvelleDuree <= 28 && optionRemise !== 1) {
                setOptionRemise(1);
            } else if (nouvelleDuree > 28 && optionRemise === 1) {
                setOptionRemise(2);
            }
        }
    }, [dateRemiseEnVigueur, contratSuspendu, isDataLoaded]);

    const { PlayCircleOutlined, CalendarOutlined, DollarOutlined, InfoCircleOutlined } = require('@ant-design/icons');

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
    };

    const calculatePreview = () => {
        if (!contratSuspendu || !isDataLoaded) {
            console.log('Pas de calcul - données non prêtes:', { contratSuspendu: !!contratSuspendu, isDataLoaded });
            return;
        }

        console.log('Calcul du preview avec:', {
            optionRemise,
            dureeeSuspension,
            primeNette: contratSuspendu.primeNette,
            fraisAccessoires
        });

        const primeOriginale = contratSuspendu.primeNette || 0;
        let ristourne = 0;
        let nouvelleEcheance = null;
        let description = "";

        const dateEcheanceOriginale = dayjs(contratSuspendu.dateEcheance);
        const dateSuspension = dayjs(contratSuspendu.dateEffet);
        const isContratEchu = dateRemiseEnVigueur.isAfter(dateEcheanceOriginale);

        switch (optionRemise) {
            case 1: // Sans prorogation ni ristourne
                description = "Remise en vigueur sans prorogation ni ristourne";
                nouvelleEcheance = dateEcheanceOriginale;
                break;

            case 2: // Avec ristourne
                if (isContratEchu) {
                    // Contrat échu : ristourne de la suspension à l'échéance
                    const joursRestants = dateEcheanceOriginale.diff(dateSuspension, 'day');
                    ristourne = (primeOriginale * joursRestants * 0.75) / 365;
                    description = "Remise en vigueur avec ristourne (contrat échu)";
                } else {
                    // Contrat non échu : ristourne sur la durée de suspension
                    ristourne = (primeOriginale * dureeeSuspension * 0.75) / 365;
                    description = "Remise en vigueur avec ristourne";
                }
                console.log("Ristourne:", ristourne, "Durée:", dureeeSuspension, "Prime:", primeOriginale);
                nouvelleEcheance = dateEcheanceOriginale;
                break;

            case 3: // Report d'échéance
                const joursReport = Math.floor(dureeeSuspension * 0.75);
                nouvelleEcheance = dateEcheanceOriginale.add(joursReport, 'day');
                description = `Report d'échéance de ${joursReport} jours`;
                break;

            default:
                break;
        }

        const newPreview = {
            description,
            ristourne,
            nouvelleEcheance,
            fraisAccessoires,
            taxes: (fraisAccessoires - ristourne) * 0.06,
            totalAPayer: (fraisAccessoires - ristourne) * 1.06
        };

        console.log('Preview calculé:', newPreview);
        setPreview(newPreview);
    };

    // useEffect pour recalculer le preview quand les données changent
    useEffect(() => {
        if (isDataLoaded) {
            console.log('Recalcul du preview déclenché');
            calculatePreview();
        }
    }, [optionRemise, contratSuspendu, dureeeSuspension, dateRemiseEnVigueur, fraisAccessoires, isDataLoaded]);

    const navigate = useNavigate();

    const enterLoading = () => setLoading(true);
    const outLoading = () => setLoading(false);

    const handleRemiseEnVigueurSubmit = async () => {
        try {
            await form.validateFields();
            enterLoading();

            const payload = {
                contratCodeId: props?.contrat?.codeId,
                dateEffet: dateRemiseEnVigueur.format('YYYY-MM-DD'),
                optionRemise,
                dureeeSuspension,
                ristourne: preview?.ristourne || 0,
                dateEcheance: preview?.nouvelleEcheance?.format('YYYY-MM-DD'),
                accessoires: fraisAccessoires,
                primeTotale: preview?.totalAPayer || 0,
                taxes: preview?.taxes || 0,
                primeNette: preview?.ristourne || 0
            };

            console.log(payload);

            const response = await axiosInstance.put(
                `/api/contrat/remise-en-vigueur`,
                JSON.stringify(payload)
            );

            toast.success("Remise en vigueur effectuée avec succès!");
            setModalRemiseEnVigueur(false);
            props.update(false);
            navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
            window.location.reload();

        } catch (error) {
            console.error(error);
            toast.error("Erreur lors de la remise en vigueur");
        } finally {
            outLoading();
        }
    };

    const handleCancel = () => {
        setModalRemiseEnVigueur(false);
        setIsDataLoaded(false); // Réinitialiser pour le prochain chargement
        props.update(false);
    };

    const getOptionDescription = (option) => {
        switch (option) {
            case 1:
                return "Réactivation immédiate sans modification de prime ni d'échéance";
            case 2:
                return "Réactivation avec calcul d'une ristourne correspondant à 75% de la période de suspension";
            case 3:
                return "Réactivation avec report de l'échéance de 75% de la durée de suspension";
            default:
                return "";
        }
    };

    const isOptionDisabled = (option) => {
        // Option 1 uniquement disponible si suspension <= 28 jours
        if (option === 1 && dureeeSuspension > 28) {
            return true;
        }
        return false;
    };

    return (
        <SDIModal
            title="REMISE EN VIGUEUR DE CONTRAT"
            centered
            confirmLoading={loading}
            open={modalRemiseEnVigueur}
            onOk={isContratSuspendu ? handleRemiseEnVigueurSubmit : undefined}
            okButtonProps={{ disabled: !isContratSuspendu }}
            onCancel={handleCancel}
            cancelText={!isContratSuspendu ? "Fermer" : "Annuler"}
            okText={!isContratSuspendu ? undefined : "Valider la remise en vigueur"}
            width={900}
            zIndex={1940}
        >
            <ConfigProvider locale={frFR}>
                <div className="remise-en-vigueur-content">
                    <AvenantAgencySelector
                        organismeCode={props?.contrat?.organisme?.code}
                        defaultAgence={props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
                    />
                    {/* Alerte si le contrat n'est pas suspendu */}
                    {!isContratSuspendu && (
                        <Alert
                            message="Contrat non suspendu"
                            description="Ce contrat n'est pas actuellement suspendu. La remise en vigueur n'est possible que pour les contrats suspendus."
                            type="error"
                            icon={<InfoCircleOutlined />}
                            showIcon
                            className="mb-4"
                        />
                    )}

                    {/* Contenu principal - affiché seulement si le contrat est suspendu */}
                    {isContratSuspendu && (
                        <>
                            {/* Informations sur la suspension */}
                            {contratSuspendu && (
                                <Card className="mb-4" size="small" title="Informations sur la suspension">
                                    <Row gutter={16}>
                                        <Col span={8}>
                                            <Statistic
                                                title="Date de suspension"
                                                value={dayjs(contratSuspendu.dateSuspension).format('DD/MM/YYYY')}
                                                prefix={<CalendarOutlined />}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Durée de suspension"
                                                value={`${dureeeSuspension} jours`}
                                                valueStyle={{ color: dureeeSuspension > 28 ? '#cf1322' : '#3f8600' }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Échéance originale"
                                                value={dayjs(contratSuspendu.dateEcheance).format('DD/MM/YYYY')}
                                                prefix={<CalendarOutlined />}
                                            />
                                        </Col>
                                    </Row>
                                </Card>
                            )}

                            {/* Avertissement selon la durée */}
                            {isDataLoaded && (dureeeSuspension > 28 ? (
                                <Alert
                                    message="Suspension de plus de 28 jours"
                                    description="La durée de suspension étant supérieure à 28 jours, vous pouvez choisir entre une remise en vigueur avec ristourne ou un report d'échéance."
                                    type="info"
                                    icon={<InfoCircleOutlined />}
                                    showIcon
                                    className="mb-4"
                                />
                            ) : (
                                <Alert
                                    message="Suspension de 28 jours ou moins"
                                    description="La durée de suspension étant de 28 jours ou moins, la remise en vigueur s'effectue sans prorogation ni ristourne."
                                    type="success"
                                    icon={<InfoCircleOutlined />}
                                    showIcon
                                    className="mb-4"
                                />
                            ))}

                            <Form form={form} layout="vertical">
                                {/* Date de remise en vigueur */}
                                <Form.Item
                                    label="Date de remise en vigueur"
                                    name="dateRemiseEnVigueur"
                                    rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                                    initialValue={dateRemiseEnVigueur}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        value={dateRemiseEnVigueur}
                                        onChange={setDateRemiseEnVigueur}
                                        disabledDate={(current) => current && current < dayjs().startOf('day')}
                                    />
                                </Form.Item>

                                {/* Options de remise en vigueur */}
                                {isDataLoaded && (
                                    <Form.Item
                                        label="Mode de remise en vigueur"
                                        name="optionRemise"
                                        rules={[{ required: true, message: 'Veuillez sélectionner une option' }]}
                                        initialValue={optionRemise}
                                    >
                                        <Radio.Group
                                            value={optionRemise}
                                            onChange={(e) => setOptionRemise(e.target.value)}
                                            className="option-remise-group"
                                        >
                                            {dureeeSuspension <= 28 && (
                                                <div className="option-item">
                                                    <Radio value={1} disabled={isOptionDisabled(1)}>
                                                        <div className="option-content">
                                                            <Title level={5} className="option-title">
                                                                Sans prorogation ni ristourne
                                                            </Title>
                                                            <Text type="secondary" className="option-description">
                                                                {getOptionDescription(1)}
                                                            </Text>
                                                            {isOptionDisabled(1) && (
                                                                <Text type="danger" className="option-disabled">
                                                                    (Non disponible - suspension {`>`} 28 jours)
                                                                </Text>
                                                            )}
                                                        </div>
                                                    </Radio>
                                                </div>
                                            )}

                                            {dureeeSuspension > 28 && (
                                                <>
                                                    <div className="option-item">
                                                        <Radio value={2}>
                                                            <div className="option-content">
                                                                <Title level={5} className="option-title">
                                                                    Avec ristourne
                                                                </Title>
                                                                <Text type="secondary" className="option-description">
                                                                    {getOptionDescription(2)}
                                                                </Text>
                                                            </div>
                                                        </Radio>
                                                    </div>

                                                    <div className="option-item">
                                                        <Radio value={3}>
                                                            <div className="option-content">
                                                                <Title level={5} className="option-title">
                                                                    Avec prorogation
                                                                </Title>
                                                                <Text type="secondary" className="option-description">
                                                                    {getOptionDescription(3)}
                                                                </Text>
                                                            </div>
                                                        </Radio>
                                                    </div>
                                                </>
                                            )}
                                        </Radio.Group>
                                    </Form.Item>
                                )}
                            </Form>

                            {/* Récapitulatif financier */}
                            {preview && (
                                <Card className="cost-summary mt-4" size="small">
                                    <Title level={5}>
                                        <DollarOutlined className="mr-2" />
                                        Récapitulatif financier
                                    </Title>
                                    <div className="cost-breakdown">
                                        <Row justify="space-between" className="cost-line">
                                            <Col>
                                                <Text>Description:</Text>
                                            </Col>
                                            <Col>
                                                <Text strong>{preview.description}</Text>
                                            </Col>
                                        </Row>

                                        {preview.nouvelleEcheance && (
                                            <Row justify="space-between" className="cost-line">
                                                <Col>
                                                    <Text>Nouvelle échéance:</Text>
                                                </Col>
                                                <Col>
                                                    <Text strong>{preview.nouvelleEcheance.format('DD/MM/YYYY HH:mm:ss')}</Text>
                                                </Col>
                                            </Row>
                                        )}

                                        <Row justify="space-between" className="cost-line">
                                            <Col>
                                                <Text>Frais accessoires:</Text>
                                            </Col>
                                            <Col>
                                                <Text>{formatCurrency(fraisAccessoires)}</Text>
                                            </Col>
                                        </Row>

                                        {preview.ristourne > 0 && (
                                            <Row justify="space-between" className="cost-line">
                                                <Col>
                                                    <Text>Ristourne (75% du prorata):</Text>
                                                </Col>
                                                <Col>
                                                    <Text className="text-green-600">
                                                        -{formatCurrency(preview.ristourne)}
                                                    </Text>
                                                </Col>
                                            </Row>
                                        )}

                                        <Divider className="my-2" />

                                        <Row justify="space-between" className="cost-line total-cost">
                                            <Col>
                                                <Text strong className="text-lg">
                                                    {preview.totalAPayer >= 0 ? "MONTANT À PAYER:" : "MONTANT À REMBOURSER:"}
                                                </Text>
                                            </Col>
                                            <Col>
                                                <Text
                                                    strong
                                                    className={`text-lg ${preview.totalAPayer >= 0 ? 'text-blue-600' : 'text-green-600'}`}
                                                >
                                                    {preview.totalAPayer >= 0 ? '' : '-'}{formatCurrency(Math.abs(preview.totalAPayer))}
                                                </Text>
                                            </Col>
                                        </Row>
                                    </div>
                                </Card>
                            )}
                        </>)}

                </div>
            </ConfigProvider>

            <style jsx>
                {`
                    .remise-en-vigueur-content {
                        max-height: 70vh;
                        overflow-y: auto;
                    }
                    
                    .option-remise-group {
                        width: 100%;
                    }
                    
                    .option-item {
                        display: block;
                        margin-bottom: 16px;
                        padding: 12px;
                        border: 1px solid #d9d9d9;
                        border-radius: 6px;
                        transition: border-color 0.3s;
                    }
                    
                    .option-item:hover {
                        border-color: #1890ff;
                    }
                    
                    .option-content {
                        margin-left: 24px;
                    }
                    
                    .option-title {
                        margin: 0 0 4px 0 !important;
                        font-size: 14px !important;
                    }
                    
                    .option-description {
                        display: block;
                        font-size: 12px;
                        line-height: 1.4;
                    }
                    
                    .option-disabled {
                        display: block;
                        font-size: 11px;
                        margin-top: 4px;
                    }
                    
                    .cost-summary {
                        background-color: #f8f9fa;
                        border: 1px solid #e9ecef;
                    }
                    
                    .cost-breakdown .cost-line {
                        margin-bottom: 8px;
                    }
                    
                    .cost-breakdown .total-cost {
                        background-color: #e6f7ff;
                        padding: 8px;
                        border-radius: 4px;
                        margin-top: 8px;
                    }
                    
                    .mr-2 { margin-right: 8px; }
                    .mt-4 { margin-top: 16px; }
                    .mb-4 { margin-bottom: 16px; }
                    .my-2 { margin: 8px 0; }
                    .text-lg { font-size: 16px; }
                    .text-blue-600 { color: #1890ff; }
                    .text-green-600 { color: #52c41a; }
                `}
            </style>
        </SDIModal>
    );
};

RemiseEnVigueur.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default RemiseEnVigueur;
